import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import withWrapper from '../helpers/withWrapper';
import getTokenContract from '../services/tokenContract';
import { FaTelegramPlane } from "react-icons/fa";
import getWallet from '../services/wallet';
import { TransactionService } from '../services/transactionService';
import './registerPage.css';
import queryString from 'query-string';
import { ethers } from 'ethers';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from './modal';

const Register = ({ router }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [referrerId, setReferrerId] = useState(1);
  const [isConnected, setIsConnected] = useState(false);
  const [balance, setBalance] = useState(0);
  const [userAddress, setUserAddress] = useState('');
  const [usdtAddress, setUsdtAddress] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [termsAccepted, setIsTermsAccepted] = useState(false);

  // Get referrerId from URL params
  useEffect(() => {
    const searchParams = location.search;
    if (searchParams) {
      const { refId } = queryString.parse(searchParams);
      if (refId) {
        setReferrerId(refId);
      }
    }
  }, [location.search]);

  // Toast notification
  const notify = (message) => toast(message);

  // Connect wallet
  const connectWallet = useCallback(async () => {
    try {
      const wallet = await getWallet();
      if (!wallet.isReadOnly) {
        const browserAddresses = await wallet.provider.listAccounts();
        setUserAddress(browserAddresses[0]);
        setIsConnected(true);
      } else {
        throw new Error('Wallet not connected');
      }
    } catch (error) {
      alert(error.message);
    }
  }, []);

  // Get balance
  const getBalance = useCallback(async () => {
    try {
      const wallet = await getWallet();
      if (!wallet.isReadOnly) {
        const token = await getTokenContract();
        const balance = await token.balanceOf(userAddress);
        if (balance.gt(ethers.BigNumber.from("0"))) {
          const totalBalanceBN = balance.div(ethers.BigNumber.from("1000000"));
          const totalBalance = ethers.BigNumber.from(totalBalanceBN).toString();
          setBalance(totalBalance);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [userAddress]);

  // Get token address
  const getTokenAddress = useCallback(async () => {
    try {
      const token = await getTokenContract();
      setUsdtAddress(token.address);
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Initial setup
  useEffect(() => {
    const init = async () => {
      try {
        await connectWallet();
        await getTokenAddress();
      } catch (error) {
        alert(error.message);
      }
    };
    init();
  }, [connectWallet, getTokenAddress]);

  // Get balance when userAddress changes
  useEffect(() => {
    if (userAddress) {
      getBalance();
    }
  }, [userAddress, getBalance]);

  const openModal = () => {
    setIsOpen(true);
  }

  // Handle registration
  const handleRegister = async () => {
    if (!isConnected) {
        alert('Connect wallet');
        return;
    }

    try {
      const wallet = await getWallet();
      const transactionService = new TransactionService(0, userAddress);
      const registrationPromise = transactionService.register(referrerId);

      const newId = await toast.promise(
        registrationPromise,
        {
          pending: `Joining in process under ID: ${referrerId}`,
          success: 'Joining Successful',
          error: 'Joining failed'
        },
        {
          position: toast.POSITION.TOP_CENTER
        }
      );

      if (newId > 0) {
        navigate('/dashboard/' + newId);
      }
    } catch (error) {
      alert(error.message);
    }
  };

  // Add token to MetaMask
  const addToMetamask = async () => {
    try {
      const wasAdded = await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: usdtAddress,
            symbol: 'USDT',
            decimals: 6,
          },
        },
      });

      if (wasAdded) {
        console.log('Thanks for your interest!');
      } else {
        console.log('Your loss!');
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Handle login button
  const handleLoginButton = () => {
    navigate('/login');
  };

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return address.slice(0, 4) + '...' + address.slice(address.length - 4, address.length);
  };

  return (
    <div style={{ background: 'black' }} id="__next1">
      <ToastContainer 
        position="top-center"
        theme="colored"
        autoClose={10000}
        style={{ zIndex: 1 }}
      />
      
      {/* Modal */}
      {isOpen && (
        <Modal 
          onClose={() => setIsOpen(false)}
          onReject={() => {
            console.log('Rejected');
            setIsOpen(false);
          }}
          onAccept={() => {
            console.log('Accepted');
            setIsOpen(false);
            setIsTermsAccepted(true);
            handleRegister();
          }}
        />
      )}

      <div style={{ zIndex: 0, backgroundImage: "url('/logo/bg01.jpeg')" }} className="flex relative bg-main-bg flex-col items-center justify-center w-full min-h-screen text-white-500 px-10 sm:px-0 overflow-hidden pt-16">
        {/* Header */}
        <div style={{ zIndex: 0, backgroundColor: "rgba(0, 0, 0, 0)" }} className="fixed top-0 left-1/2 -translate-x-1/2 flex justify-center w-full px-10 py-2.5 lg:p-0 lg:max-h-screen z-999999">
          <div style={{ background: "radial-gradient(circle at 24.1% 68.8%, rgb(50, 50, 50) 0%, rgb(0, 0, 0) 99.4%)" }} className="flex justify-between items-center rounded-mini max-w-desktop-preview-bar w-full px-5 py-2 shadow-preview-bar lg:pl-10 sm:pl-5 lg:py-2.5 lg:rounded-none lg:rounded-b-mini lg:pr-0 false">
            <div className="flex w-full overflow-hidden items-center justify-between space-x-2.5 lg:items-start">
              <div className="w-full flex items-center space-x-5 lg:flex-col lg:h-full lg:items-start lg:space-x-0 lg:space-y-7.5">
                <div className="flex items-center false lg:w-full lg:pr-10 sm:pr-5">
                  <a href="/" className="header-link">
                    <img style={{ width: '50px', height: '50px' }} src="/logo/TBIGIF.gif" className="light" alt="" />
                    <img style={{ width: '50px', height: '50px' }} src="/logo/TBIGIF.gif" className="dark" alt="" />
                  </a>
                </div>
              </div>
              {userAddress === '' ? (
                <button className="flex justify-center items-center text-center text-base font-bold text-white rounded-mini sm:text-sm outline-none px-5 py-3 bg-main-bg hover:bg-black-500 whitespace-nowrap lg:hidden" onClick={connectWallet} id="connectWallet">
                  Connect wallet
                </button>
              ) : (
                <div className="flex justify-center items-center text-center text-base font-bold text-white rounded-mini sm:text-sm outline-none px-5 py-2.5 hover:bg-line-gray active:bg-active-gray text-white font-normal rounded items-center">
                  {formatAddress(userAddress)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ padding: "20px 40px 40px", marginBottom: "50px", maxWidth: "1000px", background: "rgba(22,22,32,.5)" }} className="flex flex-col flex-1 w-full max-w-desktop-login mt-7.5 sm:mt-0">
          <div className="flex flex-1 items-center justify-between w-full sm:flex-col px-3">
            <div className="flex flex-1 flex-col items-start mr-10 sm:mr-0 sm:items-stretch sm:max-w-full sm:p-5">
              <div className="flex flex-col sm:flex-1">
                <span className="inline-block text-two-half text-white mb-2 sm:mb-7.5 sm:text-2xl">Join Infinity Matrix</span>
                <span style={{ fontSize: "18px", color: "white" }} className="inline-block text-two-half text-white mb-10 sm:mb-7.5 sm:text-3xl">Requires 10 USDT</span>
                
                <div className="flex flex-col mb-10 sm:mb-7.5">
                  <div className="w-full relative flex flex-col flex-grow-0 flex-shrink-0">
                    <label className="mb-2.5 text-white-500 sm:text-sm">Upline id</label>
                    <div className="w-full relative">
                      <input 
                        style={{ background: '#646466', color: "#ffff00" }}
                        className="w-full bg-white-100 border-2 border-transparent rounded-mini py-3 px-5 text-white outline-none focus:border-2 focus:border-main-blue false"
                        onChange={(e) => setReferrerId(e.target.value)} 
                        value={referrerId} 
                        type="number" 
                        placeholder="Upline" 
                        id="referralInput" 
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-5">
                  <div className="flex flex-col items-start">
                    <div className="flex">
                      <i className="las la-info-circle text-3xl text-base"></i>
                      <div className="flex flex-wrap items-center ml-2.5 leading-5 text-base whitespace-nowrap" id="regbalance">
                        <span>Balance: <span style={{ color: "#ffff00" }}> {balance} </span> USDT</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex">
                <button 
                  style={{ width: '203px', height: '50px', background: 'white' }}
                  className="flex justify-center items-center text-center text-base font-bold text-black rounded-mini sm:text-sm outline-none px-5 py-4 bg-blue-600 hover:bg-blue-400 active:bg-active-red-600 mt-10 py-5 !px-10 sm:py-5 sm:mt-7.5"
                  onClick={openModal} 
                  id="nextButton"
                >
                  REGISTER
                </button>
              </div>
              <br />
              
           {/*   <div className="text-lg sm:text-md">
                Read: <span style={{ color: "#ffc107" }}> <a href='/guideline.pdf' target="_blank">Guideline (Link) </a></span>
              </div> */}
            </div>

            {/* Right Side Info Card */}
            <div style={{ maxWidth: '440px' }} className="bg-white-100 rounded p-10 max-w-desktop-reg-info-card w-full sm:mt-5 flex-shrink h-auto flex flex-col sm:min-h-auto sm:mb-10 sm:max-w-full">
              <div className="flex flex-1 items-start w-full">
                <div className="flex flex-col">
                  <span className="flex items-center text-white text-2xl font-bold mb-5 sm:mb-2.5">
                    <svg className="inline w-6 h-6 mr-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="24" height="24" rx="12" fill="#fff"></rect>
                      <path fillRule="evenodd" clipRule="evenodd" d="M11.25 8a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM10.25 12a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 .75.75v3.25H13a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75v-3.25H11a.75.75 0 0 1-.75-.75Z" fill="#343A4D"></path>
                    </svg>
                    How to Invest
                  </span>
                  <div className="flex flex-col text-white">
                    <span>Access the website via Metamask mobile app or use browser extension. For Mobile users use Trust wallet app or Tocken Pocket (TP)</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-1 items-start w-full mt-5">
                <div className="flex flex-col">
                  <span className="flex items-center text-white text-2xl font-bold mb-5 sm:mb-2.5">
                    <svg className="inline w-6 h-6 mr-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="24" height="24" rx="12" fill="#fff"></rect>
                      <path fillRule="evenodd" clipRule="evenodd" d="M11.25 8a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM10.25 12a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 .75.75v3.25H13a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75v-3.25H11a.75.75 0 0 1-.75-.75Z" fill="#343A4D"></path>
                    </svg>
                    How To Access Dashboard
                  </span>
                  <div className="flex flex-col text-white">
                    <span>You'll automatically navigate to Dashboard after joining OR Access Your Dashboard Via Homepage Login Button</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-1 items-start w-full mt-5">
                <div className="flex flex-col">
                  <span className="flex items-center text-white text-2xl font-bold mb-5 sm:mb-2.5">
                    <svg className="inline w-6 h-6 mr-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="24" height="24" rx="12" fill="#fff"></rect>
                      <path fillRule="evenodd" clipRule="evenodd" d="M11.25 8a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 0 1.5H12a.75.75 0 0 1-.75-.75ZM10.25 12a.75.75 0 0 1 .75-.75h1a.75.75 0 0 1 .75.75v3.25H13a.75.75 0 0 1 0 1.5h-1a.75.75 0 0 1-.75-.75v-3.25H11a.75.75 0 0 1-.75-.75Z" fill="#343A4D"></path>
                    </svg>
                    Already joined?
                  </span>
                  <div className="flex flex-col text-white">
                    <button className="btn mt-2" onClick={handleLoginButton}>Go to Login</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ zIndex: "1000", backgroundImage: "url('/logo/bg01.jpeg')" }} className="diamond-footer">
        <div className="footer-widget-area">
          <div className="flex justify-center">
            <div className="row">
              <div className="col-lg-3 col-md-4 widget-area logo_clear_first">
                <div className="widget widget-html" style={{ paddingTop: "30px" }}></div>
              </div>
              <div className="lg:col-span-6 md:col-span-4 widget-area">
                <div className="widget widget-html text-center">
                  <h2 className="widget-title">Social Media</h2>
                  <div className="socials">
                    <ul style={{ color: "white" }}>
                      <li>
                        <a target="_blank" href="https://t.me/tbi_2024" rel="noopener noreferrer">
                          <FaTelegramPlane className="telegram-icon" style={{ color: '#ffc107' }} />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default withWrapper(Register);